import * as React from "react";

import { useAppContext } from '../../../../../app/provider/useAppContext';
import { ShieldIcon } from "../../../../../assets/icons/basket/ShieldIcon";
import AmexSvg from "../../../../../assets/icons/cards/Amex.svg";
import DinersClubSvg from "../../../../../assets/icons/cards/DinersClub.svg";
import DiscoverSvg from "../../../../../assets/icons/cards/Discover.svg";
import JcbSvg from "../../../../../assets/icons/cards/JCB.svg";
import MastercardSvg from "../../../../../assets/icons/cards/Mastercard.svg";
import UnionPaySvg from "../../../../../assets/icons/cards/UnionPay.svg";
import VisaSvg from "../../../../../assets/icons/cards/Visa.svg";
import ticketIcon from "../../../../../assets/icons/orderSummary/ticketIcon.svg";
import mailIcon from "../../../../../assets/icons/share-basket/mail.svg";
import Button from '../../../../../shared/components/Button';
import { CTLineItem, Money, AddonItem, SummaryDisplayItem } from "../../../types/cartTypes";
import { groupItemsByDelegateRate, ItemGroup } from "../../../utils/cartGrouping";
import LineItemPriceInfo from "../../lineItemPriceInfo";

import { safeMoney, extractMoney } from "./basketUtils";

/* ------------------------------
   COMPONENT
-------------------------------- */
interface OrderSummaryProps {
  lineItems?: SummaryDisplayItem[] | CTLineItem[];
  effectiveSubtotal?: Money;
  onCheckout?: () => void;
  onShare?: () => void;
}

const OrderSummary = ({
  lineItems = [],
  effectiveSubtotal,
  onCheckout,
  onShare,
}: OrderSummaryProps) => {
  const [context] = useAppContext();

  const locale =
    context?.locale ||
    sessionStorage.getItem("locale") ||
    "en";

  const currency =
    context?.currency ||
    sessionStorage.getItem("currency") ||
    effectiveSubtotal?.currencyCode ||
    "USD";

  /* ------------------------------
     SUBTOTAL
  -------------------------------- */
  const computedSubtotal = React.useMemo(() => 
    effectiveSubtotal && typeof effectiveSubtotal.centAmount === "number"
      ? effectiveSubtotal
      : (lineItems as SummaryDisplayItem[]).reduce(
        (acc, li: SummaryDisplayItem) => {
          const qty = li.quantity || 1;
          const base = extractMoney(li, currency);
          acc.centAmount += base.centAmount * qty;
          acc.currencyCode = base.currencyCode || acc.currencyCode;

          const addons = li.addons || li.custom?.fields?.addons;
          if (Array.isArray(addons)) {
            addons.forEach((addon: AddonItem) => {
              const m = addon.total || addon.price;
              const addonMoney = safeMoney(m as Money | { centAmount: number; currencyCode: string }, acc.currencyCode);
              acc.centAmount += addonMoney.centAmount * qty;
            });
          }

          return acc;
        },
        { centAmount: 0, currencyCode: currency } as Money
      ),
  [effectiveSubtotal, lineItems, currency]);

  const groupedItems = React.useMemo(() => groupItemsByDelegateRate(lineItems as CTLineItem[]), [lineItems]);

  return (
    <>
      {/* ======================================================
          DESKTOP VIEW — EXACT ORIGINAL (UNTOUCHED)
      ====================================================== */}
      <aside
        className="hidden md:flex w-[350px] flex-col gap-[20px] p-[20px] rounded-[12px] bg-[#F9FAFB]"
      >
        {/* HEADING */}
        <div className="w-[310px] h-[24px] flex items-center">
          <h3 className="text-[18px] leading-[24px] font-semibold text-[#182230]">
            Order summary
          </h3>
        </div>

        {/* PRODUCT LIST */}
        <div className="flex flex-col">
          {groupedItems.map((group: ItemGroup) => (
            <div key={group.key} className="flex flex-col">
              {group.key !== 'Other' && (
                <div className="font-sans font-normal text-[12px] leading-[18px] text-[#667085] mt-1 mb-[8px]">
                  Attendee Type: {group.key}
                </div>
              )}

              {group.items.map((li: SummaryDisplayItem, idx: number) => {
                const qty = li.quantity ?? 1;
                const name = li.productName || (li.name?.[locale] || Object.values(li.name || {})[0] || "Product");

                return (
                  <div
                    key={li.id || idx}
                    className={`w-[310px] flex flex-col border-b border-[#EAECF0] pb-[20px] transition-all last:border-b-0 last:pb-0 mb-[12px]`}
                  >
                    {/* Ticket row */}
                    <div className="flex items-center h-[18px] gap-[2px] text-[12px] leading-[18px] text-[#667085]">
                      <img
                        src={ticketIcon}
                        alt=""
                        className="w-[12.8px] h-[12.8px]"
                      />
                      <span>x</span>
                      <span>{qty}</span>
                    </div>

                    {/* Product row */}
                    <div className="flex items-center justify-between gap-[8px] w-[310px] h-[24px]">
                      <span className="truncate w-[238px] h-[24px] text-[14px] text-[#182230] font-medium">
                        {name}
                      </span>
                      <span className="w-[64px] h-[24px] text-[14px] text-[#182230] text-right font-medium">
                        <LineItemPriceInfo
                          price={{
                            value: extractMoney(li, currency),
                          }}
                          locale={locale}
                        />
                      </span>
                    </div>

                    {/* Add-ons */}
                    {(() => {
                      const addons = li.addons || li.custom?.fields?.addons;
                      if (!Array.isArray(addons) || addons.length === 0) return null;

                      return (
                        <div className="flex flex-col">
                          {[...new Map(addons.map((a: AddonItem) => [a.name, a])).values()].map((a: AddonItem, i) => {
                            const money = safeMoney(a.total || a.price, currency);
                            const multiplied = {
                              ...money,
                              centAmount: money.centAmount * qty,
                              currencyCode: money.currencyCode,
                            };

                            return (
                              <div
                                key={i}
                                className="flex items-center gap-[8px] w-[310px] h-[18px]"
                              >
                                <span className="truncate w-[232px] h-[18px] text-[12px] leading-[18px] text-[#667085]">
                                  + {a.name}
                                </span>
                                <span className="w-[70px] h-[18px] text-[12px] leading-[18px] text-[#667085] text-right">
                                  <LineItemPriceInfo
                                    price={{ value: multiplied }}
                                    locale={locale}
                                  />
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* SUBTOTAL */}
        <div className="w-[310px] h-[62px]">
          <div className="flex justify-between items-center w-[310px] h-[26px]">
            <span className="font-semibold text-[14px] text-[#182230]">
              Sub Total:
            </span>
            <span className="font-semibold text-[18px] text-[#182230] text-right">
              <LineItemPriceInfo
                price={{ value: safeMoney(computedSubtotal, currency) }}
                locale={locale}
              />
            </span>
          </div>

          <div className="text-right text-[12px] leading-[18px] text-[#667085]">
            + tax and discount
            <br />
            applied at checkout
          </div>
        </div>

        {/* CHECKOUT */}
        <div className="w-[310px] h-[88px] flex flex-col gap-[8px]">
          <Button
            onClick={onCheckout}
            variant="primary"
            className="w-[310px] h-[40px] flex items-center justify-center gap-[6px] rounded-[6px] bg-[#030407]"
          >
            <span className="text-[12px] font-bold text-white">
              Checkout
            </span>
            <span className="w-[20px] h-[20px] flex items-center justify-center"><ShieldIcon /></span>
          </Button>
          <Button
            onClick={onShare}
            variant="secondary"
            className="w-[310px] h-[40px] flex items-center justify-center gap-[6px] rounded-[6px] border border-[#667085]"
          >
            <span className="text-[12px] font-bold text-[#101828]">
              Share your basket
            </span>
            <img src={mailIcon} alt="" className="w-[20px] h-[20px]" />
          </Button>
        </div>

        {/* PAYMENT */}
        <div className="w-[310px] h-[82px] flex flex-col gap-[12px]">
          <div className="text-center text-[12px] text-[#667085]">
            We accept:
          </div>

          <div className="flex flex-col gap-[4px]">
            <div className="flex justify-center gap-[4px]">
              <img src={VisaSvg} className="w-[35px] h-[24px]" />
              <img src={AmexSvg} className="w-[35px] h-[24px]" />
              <img src={MastercardSvg} className="w-[35px] h-[24px]" />
              <img src={JcbSvg} className="w-[35px] h-[24px]" />
              <img src={DiscoverSvg} className="w-[35px] h-[24px]" />
            </div>
            <div className="flex justify-center gap-[4px]">
              <img src={DinersClubSvg} className="w-[35px] h-[24px]" />
              <img src={UnionPaySvg} className="w-[35px] h-[24px]" />

            </div>
          </div>
        </div>
      </aside>

      {/* ======================================================
          MOBILE VIEW — FIXED BOTTOM (6px GAP)
      ====================================================== */}
      <aside className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white h-[222px] px-[20px] py-[20px] border-t border-[#EAECF0] flex flex-col gap-0">
        <div className="flex justify-between items-center w-full h-[26px] gap-0 pt-0 border-t-0 mb-0">
          <span className="w-[67px] h-[20px] font-semibold text-[14px] leading-[20px] text-[#182230]">
            Sub Total:
          </span>
          <span className="w-[93px] h-[26px] font-semibold text-[18px] leading-[26px] text-[#182230] text-right">
            <LineItemPriceInfo
              price={{ value: safeMoney(computedSubtotal, currency) }}
              locale={locale}
            />
          </span>
        </div>

        <div className="w-full h-[18px] text-left text-[12px] leading-[18px] font-normal text-[#667085] mb-[12px]">
          + tax and discount applied at checkout
        </div>

        <Button
          onClick={onCheckout}
          variant="primary"
          className="w-full h-[40px] flex items-center justify-center gap-[6px] rounded-[6px] !bg-[#030407] mb-[8px]"
        >
          <span className="text-[12px] font-bold text-white">
            Checkout
          </span>
          <span className="w-[20px] h-[20px] flex items-center justify-center"><ShieldIcon /></span>
        </Button>

        <Button
          onClick={onShare}
          variant="secondary"
          className="w-full h-[40px] flex items-center justify-center gap-[6px] rounded-[6px] border border-[#667085] mb-[16px]"
        >
          <span className="text-[12px] font-bold text-[#101828]">
            Share your basket
          </span>
          <img src={mailIcon} alt="" className="w-[20px] h-[20px]" />
        </Button>

        <div
          className="
    w-full
    h-[18px]
    flex
    items-center
    justify-center
    gap-[8px]
  "
        >
          {/* Label */}
          <span className="text-[12px] font-normal text-[#667085] whitespace-nowrap">
            We accept:
          </span>

          {/* Icons */}
          <div className="flex items-center gap-[4px]">
            <img src={VisaSvg} className="w-[23px] h-[16px]" />
            <img src={AmexSvg} className="w-[23px] h-[16px]" />
            <img src={MastercardSvg} className="w-[23px] h-[16px]" />
            <img src={JcbSvg} className="w-[23px] h-[16px]" />
            <img src={DiscoverSvg} className="w-[23px] h-[16px]" />
            <img src={DinersClubSvg} className="w-[23px] h-[16px]" />
            <img src={UnionPaySvg} className="w-[23px] h-[16px]" />

          </div>
        </div>
      </aside>
    </>
  );
};

export default OrderSummary;
